import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const SupplierStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'BLOCKED']);
export type SupplierStatus = z.infer<typeof SupplierStatusSchema>;

export const PurchaseOrderStatusSchema = z.enum([
  'DRAFT',
  'SUBMITTED',
  'APPROVED',
  'PARTIAL_RECEIVED',
  'RECEIVED',
  'CANCELLED',
  'CLOSED',
]);
export type PurchaseOrderStatus = z.infer<typeof PurchaseOrderStatusSchema>;

export const GoodsReceiptStatusSchema = z.enum(['RECEIVED', 'PARTIAL', 'REJECTED']);
export type GoodsReceiptStatus = z.infer<typeof GoodsReceiptStatusSchema>;

export const InventoryTransferStatusSchema = z.enum(['PENDING', 'IN_TRANSIT', 'RECEIVED', 'CANCELLED']);
export type InventoryTransferStatus = z.infer<typeof InventoryTransferStatusSchema>;

export const DamageReasonSchema = z.enum(['TRANSIT', 'HANDLING', 'EXPIRY', 'THEFT', 'OTHER']);
export type DamageReason = z.infer<typeof DamageReasonSchema>;

export const SupplierBillStatusSchema = z.enum([
  'PENDING',
  'APPROVED',
  'PAID',
  'PARTIALLY_PAID',
  'DISPUTED',
  'CANCELLED',
]);
export type SupplierBillStatus = z.infer<typeof SupplierBillStatusSchema>;

export const ExpenseStatusSchema = z.enum(['DRAFT', 'SUBMITTED', 'APPROVED', 'PAID', 'REJECTED']);
export type ExpenseStatus = z.infer<typeof ExpenseStatusSchema>;

export const ExpenseCategorySchema = z.enum([
  'OFFICE',
  'UTILITIES',
  'RENT',
  'SOFTWARE',
  'MARKETING',
  'TRAVEL',
  'PROFESSIONAL_FEES',
  'REPAIRS',
  'OTHER',
]);
export type ExpenseCategory = z.infer<typeof ExpenseCategorySchema>;

// Common bits.
const Gstin = z
  .string()
  .trim()
  .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/, 'Invalid GSTIN format');
const Pan = z.string().trim().regex(/^[A-Z]{5}\d{4}[A-Z]$/, 'Invalid PAN format');
const Pincode = z.string().trim().regex(/^\d{6}$/, 'Pincode must be 6 digits');
const Ifsc = z.string().trim().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC format');

// ── Supplier ───────────────────────────────────────────────────────────────

export const SupplierSchema = z.object({
  id: Id,
  name: z.string(),
  legalName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  gstin: z.string().nullable(),
  panNumber: z.string().nullable(),
  city: z.string().nullable(),
  stateCode: z.string().nullable(),
  pincode: z.string().nullable(),
  paymentTermsDays: z.number().int().nonnegative(),
  status: SupplierStatusSchema,
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Supplier = z.infer<typeof SupplierSchema>;

export const CreateSupplierRequest = z
  .object({
    name: z.string().trim().min(2).max(200),
    legalName: z.string().trim().max(200).optional(),
    email: z.string().trim().email().optional(),
    phone: z.string().trim().max(20).optional(),
    gstin: Gstin.optional(),
    panNumber: Pan.optional(),
    addressLine1: z.string().trim().max(200).optional(),
    addressLine2: z.string().trim().max(200).optional(),
    city: z.string().trim().max(100).optional(),
    stateCode: z.string().trim().length(2).optional(),
    pincode: Pincode.optional(),
    paymentTermsDays: z.number().int().min(0).max(180).default(30),
    bankAccountNumber: z.string().trim().regex(/^\d{9,18}$/).optional(),
    bankIfsc: Ifsc.optional(),
    bankAccountHolder: z.string().trim().max(120).optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type CreateSupplierRequest = z.infer<typeof CreateSupplierRequest>;

export const UpdateSupplierRequest = CreateSupplierRequest.partial().extend({
  status: SupplierStatusSchema.optional(),
});
export type UpdateSupplierRequest = z.infer<typeof UpdateSupplierRequest>;

// ── Internal warehouse ─────────────────────────────────────────────────────

export const InternalWarehouseSchema = z.object({
  id: Id,
  code: z.string(),
  name: z.string(),
  city: z.string(),
  stateCode: z.string(),
  pincode: z.string(),
  isDefault: z.boolean(),
  active: z.boolean(),
  createdAt: IsoDateTime,
});
export type InternalWarehouse = z.infer<typeof InternalWarehouseSchema>;

export const CreateInternalWarehouseRequest = z
  .object({
    code: z.string().trim().regex(/^[A-Z0-9_-]{2,20}$/, 'Code must be uppercase alphanumeric'),
    name: z.string().trim().min(2).max(120),
    addressLine1: z.string().trim().min(2).max(200),
    city: z.string().trim().min(2).max(100),
    stateCode: z.string().trim().length(2),
    pincode: Pincode,
    isDefault: z.boolean().optional(),
  })
  .strict();
export type CreateInternalWarehouseRequest = z.infer<typeof CreateInternalWarehouseRequest>;

// ── Purchase order ────────────────────────────────────────────────────────

const POItemInput = z
  .object({
    description: z.string().trim().min(2).max(300),
    sku: z.string().trim().max(64).optional(),
    hsnSac: z.string().trim().max(16).optional(),
    qty: z.number().int().positive(),
    unitCostPaise: Paise.refine((v) => v > 0),
    gstRate: z.number().int().min(0).max(28).default(0),
  })
  .strict();

export const PurchaseOrderItemSchema = z.object({
  id: Id,
  description: z.string(),
  sku: z.string().nullable(),
  hsnSac: z.string().nullable(),
  qty: z.number().int(),
  unitCostPaise: Paise,
  gstRate: z.number().int(),
  taxPaise: Paise,
  lineTotalPaise: Paise,
  receivedQty: z.number().int(),
});
export type PurchaseOrderItem = z.infer<typeof PurchaseOrderItemSchema>;

export const PurchaseOrderSchema = z.object({
  id: Id,
  poNumber: z.string(),
  supplierId: Id,
  warehouseId: Id.nullable(),
  status: PurchaseOrderStatusSchema,
  itemsSubtotalPaise: Paise,
  taxPaise: Paise,
  shippingPaise: Paise,
  discountPaise: Paise,
  grandTotalPaise: Paise,
  expectedDeliveryAt: IsoDateTime.nullable(),
  approvedAt: IsoDateTime.nullable(),
  cancelledAt: IsoDateTime.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;

export const PurchaseOrderDetailSchema = PurchaseOrderSchema.extend({
  items: z.array(PurchaseOrderItemSchema),
});
export type PurchaseOrderDetail = z.infer<typeof PurchaseOrderDetailSchema>;

export const CreatePurchaseOrderRequest = z
  .object({
    supplierId: Id,
    warehouseId: Id.optional(),
    expectedDeliveryAt: IsoDateTime.optional(),
    shippingPaise: Paise.optional(),
    discountPaise: Paise.optional(),
    notes: z.string().trim().max(2000).optional(),
    items: z.array(POItemInput).min(1).max(200),
  })
  .strict();
export type CreatePurchaseOrderRequest = z.infer<typeof CreatePurchaseOrderRequest>;

// ── GRN (goods receipt note) ──────────────────────────────────────────────

const GrnItemInput = z
  .object({
    purchaseOrderItemId: Id,
    qtyReceived: z.number().int().nonnegative(),
    qtyRejected: z.number().int().nonnegative().default(0),
    rejectReason: z.string().trim().max(500).optional(),
    /// Optional batch metadata. If qtyReceived > 0 and batchCode missing, server auto-generates one.
    batchCode: z.string().trim().max(64).optional(),
    manufacturedAt: IsoDateTime.optional(),
    expiryAt: IsoDateTime.optional(),
  })
  .strict()
  .refine((v) => v.qtyReceived + v.qtyRejected > 0, {
    message: 'At least one of qtyReceived / qtyRejected must be positive',
  });

export const CreateGrnRequest = z
  .object({
    purchaseOrderId: Id,
    warehouseId: Id,
    notes: z.string().trim().max(2000).optional(),
    items: z.array(GrnItemInput).min(1).max(200),
  })
  .strict();
export type CreateGrnRequest = z.infer<typeof CreateGrnRequest>;

export const GoodsReceiptNoteSchema = z.object({
  id: Id,
  grnNumber: z.string(),
  purchaseOrderId: Id,
  warehouseId: Id,
  status: GoodsReceiptStatusSchema,
  receivedAt: IsoDateTime,
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type GoodsReceiptNote = z.infer<typeof GoodsReceiptNoteSchema>;

// ── Inventory batch + transfer + damage ────────────────────────────────────

export const InventoryBatchSchema = z.object({
  id: Id,
  warehouseId: Id,
  itemKey: z.string(),
  itemDescription: z.string(),
  batchCode: z.string(),
  qtyReceived: z.number().int(),
  qtyOnHand: z.number().int(),
  unitCostPaise: Paise,
  expiryAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type InventoryBatch = z.infer<typeof InventoryBatchSchema>;

export const WarehouseValuationSchema = z.object({
  warehouseId: Id,
  itemCount: z.number().int().nonnegative(),
  totalQty: z.number().int().nonnegative(),
  /// Sum of qtyOnHand × unitCostPaise across all batches in this warehouse.
  totalValuePaise: Paise,
});
export type WarehouseValuation = z.infer<typeof WarehouseValuationSchema>;

export const CreateTransferRequest = z
  .object({
    fromWarehouseId: Id,
    toWarehouseId: Id,
    itemKey: z.string().trim().min(1).max(120),
    qty: z.number().int().positive(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict()
  .refine((v) => v.fromWarehouseId !== v.toWarehouseId, {
    message: 'fromWarehouseId and toWarehouseId must differ',
    path: ['toWarehouseId'],
  });
export type CreateTransferRequest = z.infer<typeof CreateTransferRequest>;

export const InventoryTransferSchema = z.object({
  id: Id,
  transferNumber: z.string(),
  fromWarehouseId: Id,
  toWarehouseId: Id,
  status: InventoryTransferStatusSchema,
  itemKey: z.string(),
  itemDescription: z.string(),
  qty: z.number().int(),
  totalCostPaise: Paise,
  dispatchedAt: IsoDateTime.nullable(),
  receivedAt: IsoDateTime.nullable(),
  cancelledAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type InventoryTransfer = z.infer<typeof InventoryTransferSchema>;

export const ReportDamageRequest = z
  .object({
    warehouseId: Id,
    itemKey: z.string().trim().min(1).max(120),
    qty: z.number().int().positive(),
    reason: DamageReasonSchema,
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type ReportDamageRequest = z.infer<typeof ReportDamageRequest>;

export const DamageLogSchema = z.object({
  id: Id,
  warehouseId: Id,
  itemKey: z.string(),
  itemDescription: z.string(),
  qty: z.number().int(),
  reason: DamageReasonSchema,
  writeOffPaise: Paise,
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type DamageLog = z.infer<typeof DamageLogSchema>;

// ── Supplier bill ─────────────────────────────────────────────────────────

export const CreateSupplierBillRequest = z
  .object({
    supplierId: Id,
    purchaseOrderId: Id.optional(),
    supplierInvoiceNo: z.string().trim().min(1).max(64),
    subtotalPaise: Paise.refine((v) => v > 0),
    taxPaise: Paise.optional(),
    /// If omitted, server computes dueAt = today + supplier.paymentTermsDays.
    dueAt: IsoDateTime.optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type CreateSupplierBillRequest = z.infer<typeof CreateSupplierBillRequest>;

export const SupplierBillSchema = z.object({
  id: Id,
  billNumber: z.string(),
  supplierInvoiceNo: z.string(),
  supplierId: Id,
  purchaseOrderId: Id.nullable(),
  status: SupplierBillStatusSchema,
  subtotalPaise: Paise,
  taxPaise: Paise,
  totalPaise: Paise,
  paidPaise: Paise,
  dueAt: IsoDateTime,
  approvedAt: IsoDateTime.nullable(),
  paidAt: IsoDateTime.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type SupplierBill = z.infer<typeof SupplierBillSchema>;

export const PaySupplierBillRequest = z
  .object({
    /// Defaults to the remaining balance of the bill.
    amountPaise: Paise.optional(),
    bankRef: z.string().trim().max(100).optional(),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type PaySupplierBillRequest = z.infer<typeof PaySupplierBillRequest>;

// ── Expenses ─────────────────────────────────────────────────────────────

export const CreateExpenseRequest = z
  .object({
    category: ExpenseCategorySchema,
    description: z.string().trim().min(2).max(500),
    amountPaise: Paise.refine((v) => v > 0),
    taxPaise: Paise.optional(),
    payeeName: z.string().trim().max(200).optional(),
    receiptFileId: Id.optional(),
    incurredAt: IsoDateTime,
    notes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type CreateExpenseRequest = z.infer<typeof CreateExpenseRequest>;

export const ExpenseSchema = z.object({
  id: Id,
  refNumber: z.string(),
  category: ExpenseCategorySchema,
  description: z.string(),
  amountPaise: Paise,
  taxPaise: Paise,
  totalPaise: Paise,
  status: ExpenseStatusSchema,
  payeeName: z.string().nullable(),
  receiptFileId: z.string().nullable(),
  incurredAt: IsoDateTime,
  approvedAt: IsoDateTime.nullable(),
  paidAt: IsoDateTime.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type Expense = z.infer<typeof ExpenseSchema>;

export const ApproveDecisionRequest = z
  .object({
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type ApproveDecisionRequest = z.infer<typeof ApproveDecisionRequest>;

export const RejectDecisionRequest = z
  .object({
    reason: z.string().trim().min(4).max(500),
  })
  .strict();
export type RejectDecisionRequest = z.infer<typeof RejectDecisionRequest>;
